import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { saveSession, isLoggedIn } from '../services/auth';
import PhoneInput from '../components/PhoneInput';
import GoogleButton from '../components/GoogleButton';

const STEPS = ['Details', 'Verify Phone', 'Done'];

function EyeIcon({ show }) {
  return show
    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
}

export default function RegisterPage() {
  const navigate      = useNavigate();
  const [step, setStep] = useState(1);   // 1=form, 2=otp
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirm: '',
  });
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreeTerms, setAgreeTerms]   = useState(false);
  const [agreeRewards, setAgreeRewards] = useState(false);
  const [otp, setOtp]         = useState(['', '', '', '', '', '']);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCount] = useState(0);
  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => { if (isLoggedIn()) navigate('/dashboard'); }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => setCount(c => c - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [countdown]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Step 1: send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async e => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6)       { setError('Password must be at least 6 characters'); return; }
    if (!form.phone)                     { setError('Phone number is required'); return; }
    if (!agreeTerms)   { setError('You must agree to the Terms & Conditions and referral reward policy.'); return; }
    if (!agreeRewards) { setError('You must acknowledge that rewards are subject to verification, visa approval, and enrollment.'); return; }

    setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/send-otp', { phone: form.phone });
      setInfo(data.message);
      setStep(2);
      setCount(60);
      setTimeout(() => { if (otpRefs.current[0]) otpRefs.current[0].focus(); }, 100);
    } catch (err) {
      const status = err.response && err.response.status;
      const detail = err.response && err.response.data && err.response.data.detail;
      if (status === 202) {
        // OTP was sent but confirmation timed out — still advance to OTP step
        setInfo(detail || 'OTP sent — check your WhatsApp.');
        setStep(2);
        setCount(60);
        setTimeout(() => { if (otpRefs.current[0]) otpRefs.current[0].focus(); }, 100);
      } else {
        setError(detail || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: handle OTP digit input ────────────────────────────────────────
  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5 && otpRefs.current[i + 1]) otpRefs.current[i + 1].focus();
  };

  const handleOtpKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0 && otpRefs.current[i - 1]) otpRefs.current[i - 1].focus();
  };

  const handleOtpPaste = e => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      if (otpRefs.current[5]) otpRefs.current[5].focus();
    }
  };

  const resendOtp = async () => {
    setError(''); setInfo('');
    setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/send-otp', { phone: form.phone });
      setInfo(data.message);
      setOtp(['', '', '', '', '', '']);
      setCount(60);
      if (otpRefs.current[0]) otpRefs.current[0].focus();
    } catch (err) {
      const status = err.response && err.response.status;
      const detail = err.response && err.response.data && err.response.data.detail;
      if (status === 202) {
        setInfo(detail || 'OTP sent — check your WhatsApp.');
        setOtp(['', '', '', '', '', '']);
        setCount(60);
        if (otpRefs.current[0]) otpRefs.current[0].focus();
      } else {
        setError(detail || 'Failed to resend OTP');
      }
    } finally { setLoading(false); }
  };

  // ── Step 2: register with OTP ─────────────────────────────────────────────
  const handleRegister = async e => {
    e.preventDefault();
    setError('');
    const otpValue = otp.join('');
    if (otpValue.length < 6) { setError('Please enter the complete 6-digit OTP'); return; }

    setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/register', {
        name:          form.name,
        email:         form.email,
        phone:         form.phone,
        password:      form.password,
        otp:           otpValue,
      });
      saveSession(data.token, data.student);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <div className="auth-header">
          <span className="auth-logo">🎓</span>
          <h2>{step === 1 ? 'Create Account' : 'Verify WhatsApp'}</h2>
          <p>{step === 1 ? 'Join UNI GLOBAL Referral Program' : `OTP sent to ${form.phone}`}</p>
        </div>

        {/* Step indicator */}
        <div className="step-indicator">
          {STEPS.slice(0, 2).map((label, i) => (
            <React.Fragment key={label}>
              <div className={`step-dot ${step > i ? 'done' : step === i + 1 ? 'active' : ''}`}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <div className="step-label">{label}</div>
              {i < 1 && <div className={`step-line ${step > 1 ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Registration Form ── */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="auth-form">
            <div className="form-group">
              <label>Full Name</label>
              <input name="name" value={form.name} onChange={handleChange} required placeholder="Rajesh Kumar" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" />
            </div>
            <div className="form-group">
              <label>WhatsApp Phone Number</label>
              <PhoneInput name="phone" value={form.phone} onChange={handleChange} required placeholder="10-digit WhatsApp number" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Password</label>
                <div className="input-eye-wrap">
                  <input name="password" type={showPass ? 'text' : 'password'} value={form.password} onChange={handleChange} required placeholder="Min 6 characters" />
                  <button type="button" className="eye-btn" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                    <EyeIcon show={showPass} />
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <div className="input-eye-wrap">
                  <input name="confirm" type={showConfirm ? 'text' : 'password'} value={form.confirm} onChange={handleChange} required placeholder="Repeat password" />
                  <button type="button" className="eye-btn" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                    <EyeIcon show={showConfirm} />
                  </button>
                </div>
              </div>
            </div>
            <div className="consent-checks">
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                />
                <span>I agree to the Terms &amp; Conditions and referral reward policy.</span>
              </label>
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={agreeRewards}
                  onChange={e => setAgreeRewards(e.target.checked)}
                />
                <span>I understand that rewards are subject to verification, visa approval, and enrollment.</span>
              </label>
            </div>

            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Sending OTP…' : 'Send OTP to WhatsApp →'}
            </button>
          </form>
        )}
        {step === 1 && (
          <GoogleButton text="signup_with"
            onCredential={cred => navigate('/login', { state: { googleCredential: cred } })} />
        )}

        {/* ── Step 2: OTP Verification ── */}
        {step === 2 && (
          <form onSubmit={handleRegister} className="auth-form">
            {info && <div className="auth-info">{info}</div>}

            <div className="form-group">
              <label>Enter 6-digit OTP</label>
              <div className="otp-boxes" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKey(i, e)}
                    className="otp-box"
                  />
                ))}
              </div>
              <div className="otp-hint">
                {countdown > 0
                  ? `Resend OTP in ${countdown}s`
                  : <button type="button" className="btn-link" onClick={resendOtp} disabled={loading}>Resend OTP</button>
                }
              </div>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <div style={{ display: 'flex', gap: '.75rem' }}>
              <button type="button" className="btn-back" onClick={() => { setStep(1); setError(''); setOtp(['','','','','','']); }}>
                ← Back
              </button>
              <button type="submit" className="btn-primary" disabled={loading || otp.join('').length < 6}
                style={{ flex: 1 }}>
                {loading ? 'Verifying…' : 'Create Account'}
              </button>
            </div>
          </form>
        )}

        <p className="auth-switch">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}
